import { CONTEXT_MESH_HUB_ROUTE, NEW_CONTEXT_MESH_ROUTE, contextMeshHubUrl, parseContextMeshHubDeepLink } from "./contextMeshDeepLink";
import { parseEditorDeepLink } from "./editorDeepLink";
import { type FeatureStudioDeepLink, parseFeatureStudioDeepLink } from "./featureStudioDeepLink";
import { parseProductContextMeshDeepLink } from "./productContextMeshDeepLink";

export const PRODUCTS_ROUTE = "/products";
export const NEW_PRODUCT_ROUTE = `${PRODUCTS_ROUTE}/new`;
export const FILES_ROUTE = "/files";
export const INTEGRATIONS_ROUTE = "/integrations";

export type AppRouteView =
    | "projects"
    | "new-product"
    | "features"
    | "editor"
    | "uploads"
    | "integrations"
    | "context-mesh-console"
    | "new-context-mesh"
    | "refresh-context-mesh"
    | "profile";

function normalizePathname(pathname: string): string {
    return pathname.replace(/\/+$/, "") || "/";
}

export function normalizePathWithSearch(pathname: string, search = ""): string {
    const normalizedPath = normalizePathname(pathname);
    if (!search || search === "?") return normalizedPath;
    return `${normalizedPath}${search.startsWith("?") ? search : `?${search}`}`;
}

export function productIdFromSearch(search = ""): number | undefined {
    const productId = Number(new URLSearchParams(search).get("productId"));
    return Number.isSafeInteger(productId) && productId > 0 ? productId : undefined;
}

export function managementRouteForView(view: string, contextMeshId?: string | null): string | null {
    if (view !== "uploads" && view !== "integrations") return null;
    if (contextMeshId) {
        return contextMeshHubUrl(contextMeshId, view === "uploads" ? "files" : "integrations");
    }
    return view === "uploads" ? FILES_ROUTE : INTEGRATIONS_ROUTE;
}

export function viewForPath(pathname: string, search = ""): AppRouteView | null {
    const normalizedPath = normalizePathname(pathname);
    if (normalizedPath === NEW_PRODUCT_ROUTE) return "new-product";
    if (normalizedPath === PRODUCTS_ROUTE) return "projects";
    if (normalizedPath === FILES_ROUTE) return "uploads";
    if (normalizedPath === INTEGRATIONS_ROUTE) return "integrations";
    if (parseProductContextMeshDeepLink(normalizedPath)) return "projects";

    const contextMeshLink = parseContextMeshHubDeepLink(normalizedPath);
    if (contextMeshLink?.action === "create" || contextMeshLink?.action === "draft") {
        return "new-context-mesh";
    }
    if (contextMeshLink?.action === "refresh") return "refresh-context-mesh";
    if (contextMeshLink) return "context-mesh-console";
    if (parseEditorDeepLink(normalizedPath, search)) return "editor";
    if (parseFeatureStudioDeepLink(normalizedPath, search)) return "features";
    return null;
}

export function routeForView(view: string): string | null {
    if (view === "new-product") return NEW_PRODUCT_ROUTE;
    if (view === "projects") return PRODUCTS_ROUTE;
    if (view === "uploads") return FILES_ROUTE;
    if (view === "integrations") return INTEGRATIONS_ROUTE;
    if (view === "context-mesh-console") return CONTEXT_MESH_HUB_ROUTE;
    if (view === "new-context-mesh") return NEW_CONTEXT_MESH_ROUTE;
    if (view === "profile") return "/dashboard";
    return null;
}

export function featureStudioUrl(productId: number): string {
    if (!Number.isSafeInteger(productId) || productId <= 0) {
        throw new Error("A positive Product id is required");
    }
    return `${PRODUCTS_ROUTE}/${productId}`;
}

export function featureStudioReturnUrl(link: FeatureStudioDeepLink): string {
    const productUrl = featureStudioUrl(link.projectId);
    if (link.level === "PRODUCT") return productUrl;

    const nodeId = link.nodeId?.trim();
    if (!nodeId) throw new Error("A Feature Studio node id is required");

    const nodePrefix = link.level === "FEATURE" ? "F" : "S";
    const params = new URLSearchParams({
        view: "feature-studio",
        node: `${nodePrefix}:${nodeId}`
    });
    return `${productUrl}?${params.toString()}`;
}
